import { sortIcdSuggestionsByLikelihood } from "@taskhealth/shared";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    SafeAreaView,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { ActivityIndicator, Snackbar, Text } from "react-native-paper";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { IcdChipPicker } from "@/components/medications/IcdChipPicker";
import { StrengthQuestion } from "@/components/medications/StrengthQuestion";
import { SuggestionQuestion, SUPPLEMENT_ROUTES } from "@/components/medications/SuggestionQuestion";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import {
    createMedication,
    fetchAISuggestions,
    fetchMedications,
    promoteDraft,
    saveMedicationDiagnosisCodes,
    type AISuggestions,
    type Medication
} from "@/lib/api";
import { COMMON_ICD10_CODES } from "@/lib/icd10-common";
import { isPrnFrequency, mddChipSuggestions } from "@/lib/mdd-utils";
import { checkDuplicate, isStandardFrequency } from "@/lib/medication-utils";

interface IcdCode {
    code: string;
    description: string;
    likelihood?: number;
}

export default function ReviewScreen() {
    const {
        id: visitId,
        medicationName,
        dose,
        strength: strengthParam,
        strengthNotApplicable: strengthNotApplicableParam,
        route: routeParam,
        orderedDose: orderedDoseParam,
        frequency: prefillFreq,
        adminNotes: prefillNotes,
        isSupplement: isSupplementParam,
        draftId,
        fromDraft,
        fromLookup: fromLookupParam,
        lookupData: lookupDataParam,
        draftIcds: draftIcdsParam
    } = useLocalSearchParams<{
        id: string;
        medicationName: string;
        dose: string;
        strength?: string;
        strengthNotApplicable?: string;
        route?: string;
        orderedDose?: string;
        frequency?: string;
        adminNotes?: string;
        isSupplement: string;
        draftId?: string;
        fromDraft?: string;
        fromLookup?: string;
        lookupData?: string;
        draftIcds?: string;
    }>();
    const router = useRouter();
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();

    // Prevent double-navigation from snackbar timer + manual back press
    const hasNavigatedRef = useRef(false);
    const navTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => {
        return () => {
            if (navTimerRef.current) {
                clearTimeout(navTimerRef.current);
            }
        };
    }, []);

    const isSupplement = isSupplementParam === "true";
    const isFromDraft = fromDraft === "true" && !!draftId;
    const isFromLookup = fromLookupParam === "true" && !!lookupDataParam;
    const lookupEntry = isFromLookup ? JSON.parse(lookupDataParam!) : null;
    const draftIcds: IcdCode[] = draftIcdsParam ? JSON.parse(draftIcdsParam) : [];
    const initialStrength =
        strengthNotApplicableParam === "true"
            ? undefined
            : strengthParam || lookupEntry?.strength || (!isFromLookup && dose ? dose : undefined);
    const initialOrderedDose = orderedDoseParam || (isFromLookup && dose ? dose : undefined);

    const [enteredStrength, setEnteredStrength] = useState(initialStrength ?? "");
    const [strengthNotApplicable, setStrengthNotApplicable] = useState(
        strengthNotApplicableParam === "true"
    );
    const [route, setRoute] = useState(routeParam ?? lookupEntry?.route ?? "");
    const effectiveStrength = strengthNotApplicable
        ? undefined
        : enteredStrength.trim() || undefined;

    // LLM suggestions
    const [suggestions, setSuggestions] = useState<AISuggestions | null>(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(true);
    const [llmFailed, setLlmFailed] = useState(false);
    const [selectedDose, setSelectedDose] = useState<string | null>(initialOrderedDose || null);
    const [customDoseText, setCustomDoseText] = useState("");

    // Field selections — pre-fill from draft params if available
    const prefillIsCustom = !!prefillFreq && !isStandardFrequency(prefillFreq);
    const [selectedFreq, setSelectedFreq] = useState<string | null>(
        prefillFreq && !prefillIsCustom ? prefillFreq : null
    );
    const [customFreq, setCustomFreq] = useState(prefillIsCustom ? prefillFreq! : "");
    const [showCustomFreq, setShowCustomFreq] = useState(prefillIsCustom);
    const [selectedNotes, setSelectedNotes] = useState<Set<string>>(new Set());
    const [customNote, setCustomNote] = useState(prefillNotes ?? "");
    const [selectedIcds, setSelectedIcds] = useState<Set<string>>(new Set());
    const [addedIcds, setAddedIcds] = useState<IcdCode[]>(draftIcds);
    const [showErrors, setShowErrors] = useState(false);
    // MDD — offered (optional) when the chosen frequency is PRN
    const [mddChip, setMddChip] = useState<string | null>(null);
    const [customMdd, setCustomMdd] = useState("");
    const [showCustomMdd, setShowCustomMdd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState<string | null>(null);

    // Load suggestions — from our DB (instant) or LLM (API call)
    useEffect(() => {
        if (!visitId || !medicationName) {
            return;
        }

        if (isFromLookup && lookupEntry) {
            // Use our internal data — no LLM call needed
            const data: AISuggestions = {
                frequencies: lookupEntry.frequencies.map((f: { value: string }) => f.value),
                administrationNotes: lookupEntry.adminNotes.map((n: { value: string }) => n.value),
                icdCodes: lookupEntry.diagnoses.map((d: { code: string; description: string }) => ({
                    code: d.code,
                    description: d.description
                })),
                doses: lookupEntry.dosages.map((d: { value: string }) => d.value)
            };
            setSuggestions(data);
            if (data.frequencies.length > 0 && !prefillFreq) {
                setSelectedFreq(data.frequencies[0]);
            }
            setLoadingSuggestions(false);
            return;
        }

        // Fallback: call LLM
        fetchAISuggestions(
            visitId,
            medicationName,
            undefined,
            undefined,
            false,
            initialStrength,
            initialOrderedDose
        )
            .then((data) => {
                setSuggestions(data);
                if (data.frequencies.length > 0 && !prefillFreq) {
                    setSelectedFreq(data.frequencies[0]);
                }
            })
            .catch(() => setLlmFailed(true))
            .finally(() => setLoadingSuggestions(false));
    }, [visitId, medicationName, initialStrength, initialOrderedDose]);

    // Suggested chips ranked most-likely first; nurse-added codes follow.
    const icdCodes: IcdCode[] = [
        ...sortIcdSuggestionsByLikelihood(
            (suggestions?.icdCodes ?? []).map((c): IcdCode => {
                if (typeof c === "string") {
                    const found = COMMON_ICD10_CODES.find((icd) => icd.code === c);
                    return { code: c, description: found?.description ?? "" };
                }
                return c;
            })
        ),
        ...addedIcds
    ];

    const frequency = (showCustomFreq ? customFreq : selectedFreq)?.trim();
    // MDD (optional): derived chip from the chosen dose × schedule + Other.
    const askMdd = isPrnFrequency(frequency) && !isSupplement;
    const mddSuggestions = askMdd
        ? mddChipSuggestions(
              selectedDose ?? (customDoseText || initialOrderedDose || null),
              frequency
          )
        : [];
    const effectiveMdd = showCustomMdd ? customMdd.trim() || null : mddChip;
    const adminNotes = [...selectedNotes, ...(customNote ? [customNote] : [])].join(", ");

    // The amount taken at one time. Defined once so the save guard below checks
    // exactly the value the two write paths send — they used to recompute it
    // inline, which is how a medication could be blocked on frequency while its
    // dose went out empty.
    const orderedDose = (selectedDose || customDoseText || initialOrderedDose)?.trim() || undefined;

    // Never preselected — the ceiling is always the nurse's explicit tap. And a
    // tapped chip must stay visible: when a frequency/dose change re-derives the
    // suggestions and the chosen value is no longer among them, drop the
    // selection rather than silently saving a ceiling the card no longer shows.
    const mddValues = mddSuggestions.map((sg) => sg.value).join("|");
    useEffect(() => {
        if (mddChip && !showCustomMdd && !mddValues.split("|").includes(mddChip)) {
            setMddChip(null);
        }
    }, [mddChip, showCustomMdd, mddValues]);

    const handleSave = async () => {
        if (
            (!effectiveStrength && !strengthNotApplicable) ||
            !orderedDose ||
            !frequency ||
            (isSupplement ? !route.trim() : selectedIcds.size === 0)
        ) {
            setShowErrors(true);
            return;
        }

        setSaving(true);
        try {
            // Duplicate detection — check if same name + type already exists
            if (!isFromDraft) {
                const existingMeds = await fetchMedications(visitId!);
                const dup = checkDuplicate(existingMeds, medicationName!, isSupplement);
                if (dup) {
                    setSaving(false);
                    showDialog(
                        isSupplement
                            ? "Supplement Already on Profile"
                            : "Medication Already on Profile",
                        `${medicationName} is already on the patient's profile. You cannot add it again.`,
                        [{ text: "OK", style: "cancel" }]
                    );
                    return;
                }
            }

            // Build diagnosis codes first — validate before creating medication
            const codes = icdCodes.filter((c) => selectedIcds.has(c.code));
            const diagnosisCodes = codes.map((c) => {
                let desc = c.description;
                if (!desc) {
                    const found = COMMON_ICD10_CODES.find((icd) => icd.code === c.code);
                    desc = found?.description ?? c.code;
                }
                return { icdCode: c.code, description: desc };
            });

            // Validate descriptions are present for medications
            if (
                !isSupplement &&
                diagnosisCodes.some((d) => !d.description || d.description.length === 0)
            ) {
                showDialog("Error", "Diagnosis codes must have descriptions. Please try again.");
                return;
            }

            if (isFromDraft) {
                // Promote the draft instead of creating new
                await promoteDraft(visitId!, draftId!, {
                    route: route.trim() || undefined,
                    strength: effectiveStrength,
                    strengthNotApplicable,
                    orderedDose,
                    frequency: frequency || undefined,
                    // She typed her own wording, so the server stores it as
                    // typed instead of folding it onto the vocabulary.
                    frequencyIsCustom: showCustomFreq,
                    administrationNotes: adminNotes || undefined,
                    isSupplement,
                    diagnosisCodes: diagnosisCodes.length > 0 ? diagnosisCodes : undefined
                });
                setSnackbar(`${medicationName} saved`);
                navTimerRef.current = setTimeout(() => {
                    if (!hasNavigatedRef.current) {
                        hasNavigatedRef.current = true;
                        router.back();
                    }
                }, 800);
            } else {
                const med = await createMedication(visitId!, {
                    medicationName: medicationName!,
                    strength: effectiveStrength,
                    strengthNotApplicable,
                    orderedDose,
                    frequency: frequency || undefined,
                    frequencyIsCustom: showCustomFreq,
                    administrationNotes: adminNotes || undefined,
                    maxDailyDose: askMdd ? (effectiveMdd ?? undefined) : undefined,
                    isSupplement,
                    route: route.trim() || undefined
                });

                if (diagnosisCodes.length > 0) {
                    await saveMedicationDiagnosisCodes(visitId!, med.id, diagnosisCodes);
                }

                setSnackbar(`${medicationName} saved`);
                navTimerRef.current = setTimeout(() => {
                    if (!hasNavigatedRef.current) {
                        hasNavigatedRef.current = true;
                        router.dismiss(2);
                    }
                }, 800);
            }
        } catch (err) {
            showDialog("Error", "Failed to save medication. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // Skeleton placeholder
    const Shimmer = () => (
        <View style={styles.shimmer}>
            <View style={styles.shimmerLine} />
            <View style={[styles.shimmerLine, { width: "60%" }]} />
        </View>
    );

    return (
        <SafeAreaView style={styles.safe}>
            {/* Header */}
            <View style={styles.wizardHeader}>
                <TouchableOpacity
                    onPress={() => {
                        if (!hasNavigatedRef.current) {
                            hasNavigatedRef.current = true;
                            router.back();
                        }
                    }}
                    style={styles.backBtn}
                >
                    <Text style={styles.backBtnText}>{"\u2190"} Back</Text>
                </TouchableOpacity>
                <Text style={styles.stepLabel}>STEP 2 OF 2</Text>
                <Text style={styles.wizardTitle}>Review & Save</Text>
                <View style={styles.progressBar}>
                    <View style={[styles.progressFill, { width: "100%" }]} />
                </View>
            </View>

            {/* LLM failure banner */}
            {llmFailed && (
                <View style={styles.infoBanner}>
                    <Text style={styles.infoBannerText}>
                        AI suggestions unavailable. Please fill in details manually.
                    </Text>
                </View>
            )}

            <KeyboardAvoidingView
                style={styles.flexFill}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                <ScrollView
                    style={styles.scrollContent}
                    keyboardShouldPersistTaps="handled"
                    automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                >
                    {/* Summary pill */}
                    <View style={styles.summaryPill}>
                        <Text style={styles.summaryName}>{medicationName}</Text>
                        {initialStrength ? (
                            <View style={styles.doseBadge}>
                                <Text style={styles.doseBadgeText}>{initialStrength}</Text>
                            </View>
                        ) : null}
                        {isSupplement && (
                            <View style={styles.suppBadge}>
                                <Text style={styles.suppBadgeText}>Supplement</Text>
                            </View>
                        )}
                    </View>

                    {!initialOrderedDose && (
                        <SuggestionQuestion
                            label="Amount taken"
                            required
                            value={selectedDose ?? customDoseText}
                            onChange={(value) => {
                                setCustomDoseText(value);
                                setSelectedDose(value);
                            }}
                            options={suggestions?.doses ?? []}
                            loading={loadingSuggestions}
                            showError={showErrors}
                            errorMessage="Please enter the amount taken at one time"
                            placeholder="Amount per dose (e.g., 1 tablet)"
                        />
                    )}

                    {!initialStrength && (
                        <StrengthQuestion
                            visitId={visitId}
                            productName={medicationName ?? ""}
                            value={enteredStrength}
                            onChange={setEnteredStrength}
                            notApplicable={strengthNotApplicable}
                            onNotApplicableChange={setStrengthNotApplicable}
                            showError={showErrors}
                        />
                    )}
                    {isSupplement && (
                        <SuggestionQuestion
                            label="Route"
                            required
                            value={route}
                            onChange={setRoute}
                            options={SUPPLEMENT_ROUTES}
                            showError={showErrors}
                            placeholder="Enter route"
                        />
                    )}

                    {/* Frequency */}
                    <View style={styles.fieldGroup}>
                        <Text
                            style={[
                                styles.fieldLabel,
                                showErrors && !frequency && { color: "#DC2626" }
                            ]}
                        >
                            Frequency *
                        </Text>
                        {loadingSuggestions ? (
                            <Shimmer />
                        ) : (
                            <>
                                {suggestions && suggestions.frequencies.length > 0 && (
                                    <Text style={styles.fieldSublabel}>
                                        {"\u2728"} AI-suggested
                                    </Text>
                                )}
                                <View style={styles.chipGroup}>
                                    {(suggestions?.frequencies ?? []).map((freq) => (
                                        <TouchableOpacity
                                            key={freq}
                                            style={[
                                                styles.chip,
                                                selectedFreq === freq &&
                                                    !showCustomFreq &&
                                                    styles.chipSelected
                                            ]}
                                            onPress={() => {
                                                setSelectedFreq(
                                                    freq === selectedFreq ? null : freq
                                                );
                                                setShowCustomFreq(false);
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    selectedFreq === freq &&
                                                        !showCustomFreq &&
                                                        styles.chipTextSelected
                                                ]}
                                            >
                                                {freq}
                                            </Text>
                                        </TouchableOpacity>
                                    ))}
                                    <TouchableOpacity
                                        style={[
                                            styles.chip,
                                            styles.chipDashed,
                                            showCustomFreq && styles.chipSelected
                                        ]}
                                        onPress={() => {
                                            setShowCustomFreq(true);
                                            setSelectedFreq(null);
                                        }}
                                    >
                                        <Text
                                            style={[
                                                styles.chipText,
                                                showCustomFreq && styles.chipTextSelected
                                            ]}
                                        >
                                            Other
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                                {showCustomFreq && (
                                    <TextInput
                                        style={styles.customInput}
                                        placeholder="Enter custom frequency..."
                                        placeholderTextColor="#B0AECE"
                                        value={customFreq}
                                        onChangeText={setCustomFreq}
                                        autoFocus
                                        autoCorrect={false}
                                        spellCheck={false}
                                    />
                                )}
                                {showErrors && !frequency && (
                                    <Text style={styles.fieldError}>Please select a frequency</Text>
                                )}
                            </>
                        )}
                    </View>

                    {/* Max daily dose — optional, offered for PRN frequencies */}
                    {askMdd && (
                        <View style={styles.fieldGroup}>
                            <Text style={styles.fieldLabel}>
                                Max Daily Dose <Text style={styles.optionalTag}>(optional)</Text>
                            </Text>
                            <View style={styles.chipGroup}>
                                {mddSuggestions.map((sg) => (
                                    <TouchableOpacity
                                        key={sg.value}
                                        style={[
                                            styles.chip,
                                            mddChip === sg.value &&
                                                !showCustomMdd &&
                                                styles.chipSelected
                                        ]}
                                        onPress={() => {
                                            setMddChip(
                                                !showCustomMdd && mddChip === sg.value
                                                    ? null
                                                    : sg.value
                                            );
                                            setShowCustomMdd(false);
                                        }}
                                    >
                                        <Text
                                            style={[
                                                styles.chipText,
                                                mddChip === sg.value &&
                                                    !showCustomMdd &&
                                                    styles.chipTextSelected
                                            ]}
                                        >
                                            {sg.value} ({sg.explain})
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                                <TouchableOpacity
                                    style={[
                                        styles.chip,
                                        { borderStyle: "dashed" as const, borderColor: "#B0AECE" },
                                        showCustomMdd && styles.chipSelected
                                    ]}
                                    onPress={() => {
                                        setShowCustomMdd(true);
                                        setMddChip(null);
                                    }}
                                >
                                    <Text
                                        style={[
                                            styles.chipText,
                                            showCustomMdd && styles.chipTextSelected
                                        ]}
                                    >
                                        Other
                                    </Text>
                                </TouchableOpacity>
                            </View>
                            {showCustomMdd && (
                                <TextInput
                                    style={styles.customInput}
                                    placeholder="e.g., 8 tablets"
                                    placeholderTextColor="#B0AECE"
                                    value={customMdd}
                                    onChangeText={setCustomMdd}
                                    autoFocus
                                    autoCorrect={false}
                                    spellCheck={false}
                                />
                            )}
                            <Text style={styles.mddHintText}>
                                The most the patient may take in 24 hours.
                            </Text>
                        </View>
                    )}

                    {/* Administration Notes */}
                    <View style={styles.fieldGroup}>
                        <Text style={styles.fieldLabel}>Administration Notes</Text>
                        {loadingSuggestions ? (
                            <Shimmer />
                        ) : (
                            <>
                                {suggestions && suggestions.administrationNotes.length > 0 && (
                                    <Text style={styles.fieldSublabel}>
                                        {"\u2728"} AI-suggested · select all that apply (optional)
                                    </Text>
                                )}
                                <View style={styles.chipGroup}>
                                    {(suggestions?.administrationNotes ?? []).map((note) => (
                                        <TouchableOpacity
                                            key={note}
                                            style={[
                                                styles.chip,
                                                selectedNotes.has(note) && styles.chipSelected
                                            ]}
                                            onPress={() => {
                                                setSelectedNotes((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(note)) {
                                                        next.delete(note);
                                                    } else {
                                                        next.add(note);
                                                    }
                                                    return next;
                                                });
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    styles.chipText,
                                                    selectedNotes.has(note) &&
                                                        styles.chipTextSelected
                                                ]}
                                            >
                                                {note}
                                            </Text>
                                        </TouchableOpacity>
                                    ))}
                                    <TouchableOpacity style={[styles.chip, styles.chipDashed]}>
                                        <TextInput
                                            style={styles.chipInputText}
                                            placeholder="+ Add Custom"
                                            placeholderTextColor="#6B6B8D"
                                            value={customNote}
                                            onChangeText={setCustomNote}
                                            autoCorrect={false}
                                            spellCheck={false}
                                        />
                                    </TouchableOpacity>
                                </View>
                            </>
                        )}
                    </View>

                    {/* Diagnosis Codes */}
                    <View style={styles.fieldGroup}>
                        <Text
                            style={[
                                styles.fieldLabel,
                                showErrors &&
                                    selectedIcds.size === 0 &&
                                    !isSupplement && { color: "#DC2626" }
                            ]}
                        >
                            Diagnosis Codes {!isSupplement && "*"}
                        </Text>
                        {loadingSuggestions ? (
                            <Shimmer />
                        ) : (
                            <>
                                {icdCodes.length > 0 && (
                                    <Text style={styles.fieldSublabel}>
                                        {"\u2728"} Suggested by AI
                                        {!isSupplement && " · Select at least one"}
                                    </Text>
                                )}
                                <IcdChipPicker
                                    suggestedCodes={icdCodes}
                                    selectedCodes={selectedIcds}
                                    onToggle={(code) => {
                                        setSelectedIcds((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(code)) {
                                                next.delete(code);
                                            } else {
                                                next.add(code);
                                            }
                                            return next;
                                        });
                                    }}
                                    onAdd={(code) => {
                                        setAddedIcds((prev) => [...prev, code]);
                                        setSelectedIcds((prev) => new Set([...prev, code.code]));
                                    }}
                                />
                                {showErrors && selectedIcds.size === 0 && !isSupplement && (
                                    <Text style={styles.fieldError}>
                                        Please select at least one diagnosis code
                                    </Text>
                                )}
                            </>
                        )}
                    </View>
                </ScrollView>
            </KeyboardAvoidingView>

            {/* Sticky save */}
            <View style={styles.stickyBottom}>
                <TouchableOpacity
                    style={styles.saveBtn}
                    onPress={handleSave}
                    disabled={saving || loadingSuggestions}
                >
                    {saving ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : loadingSuggestions ? (
                        <Text style={styles.saveBtnText}>Loading suggestions...</Text>
                    ) : (
                        <Text style={styles.saveBtnText}>
                            Save {isSupplement ? "Supplement" : "Medication"}
                        </Text>
                    )}
                </TouchableOpacity>
            </View>

            <Snackbar
                visible={snackbar !== null}
                onDismiss={() => setSnackbar(null)}
                duration={1500}
                style={styles.snackbar}
            >
                {snackbar ? `\u2713 ${snackbar}` : ""}
            </Snackbar>
            <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: "#FAFAF9" },
    flexFill: { flex: 1 },
    wizardHeader: {
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 16,
        backgroundColor: "#FFFFFF"
    },
    backBtn: {
        marginBottom: 8
    },
    backBtnText: {
        fontSize: 14,
        fontWeight: "500",
        color: "#6B6B8D"
    },
    stepLabel: {
        fontSize: 11,
        fontWeight: "700",
        letterSpacing: 1,
        color: "#7C3AED",
        textTransform: "uppercase",
        marginBottom: 4
    },
    wizardTitle: {
        fontSize: 20,
        fontWeight: "800",
        color: "#1F1547",
        marginBottom: 10
    },
    progressBar: {
        height: 4,
        backgroundColor: "#EDE9FE",
        borderRadius: 4,
        overflow: "hidden"
    },
    progressFill: {
        height: "100%",
        backgroundColor: "#7C3AED",
        borderRadius: 4
    },
    infoBanner: {
        backgroundColor: "#F5F3FF",
        borderWidth: 1,
        borderColor: "#C4B5FD",
        borderRadius: 10,
        padding: 12,
        marginHorizontal: 20,
        marginTop: 12
    },
    infoBannerText: { fontSize: 12, color: "#7C3AED" },
    scrollContent: { flex: 1, padding: 20 },
    summaryPill: {
        backgroundColor: "#FAFAF9",
        borderWidth: 1,
        borderColor: "#EDE9FE",
        borderRadius: 12,
        padding: 12,
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginBottom: 20,
        flexWrap: "wrap"
    },
    summaryName: { fontSize: 14, fontWeight: "700", color: "#1F1547" },
    doseBadge: {
        backgroundColor: "#F5F3FF",
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 6
    },
    doseBadgeText: { fontSize: 12, fontWeight: "600", color: "#A78BFA" },
    suppBadge: {
        backgroundColor: "#EDE9FE",
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6
    },
    suppBadgeText: { fontSize: 10, fontWeight: "700", color: "#7C3AED" },
    fieldGroup: { marginBottom: 22 },
    fieldLabel: {
        fontSize: 15,
        fontWeight: "700",
        color: "#1F1547",
        marginBottom: 2
    },
    fieldSublabel: {
        fontSize: 11,
        color: "#B0AECE",
        marginBottom: 8
    },
    fieldError: { fontSize: 12, color: "#DC2626", fontWeight: "600", marginTop: 6 },
    optionalTag: { fontSize: 10, color: "#B0AECE", fontWeight: "400" },
    mddHintText: { fontSize: 11, color: "#B45309", marginTop: 8 },
    chipGroup: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
        marginTop: 6
    },
    chip: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 20,
        borderWidth: 2,
        borderColor: "#EDE9FE",
        backgroundColor: "#FFFFFF"
    },
    chipSelected: {
        borderColor: "#A78BFA",
        backgroundColor: "#A78BFA"
    },
    chipDashed: { borderStyle: "dashed", borderColor: "#B0AECE" },
    chipText: { fontSize: 13, fontWeight: "600", color: "#6B6B8D" },
    chipTextSelected: { color: "#FFFFFF" },
    chipInputText: { fontSize: 13, color: "#6B6B8D", padding: 0 },
    customInput: {
        borderWidth: 2,
        borderColor: "#EDE9FE",
        borderRadius: 12,
        padding: 14,
        fontSize: 15,
        color: "#1F1547",
        backgroundColor: "#FFFFFF",
        marginTop: 8
    },
    shimmer: { gap: 8, paddingVertical: 12 },
    shimmerLine: {
        height: 12,
        backgroundColor: "#EDE9FE",
        borderRadius: 6,
        width: "80%",
        opacity: 0.6
    },
    stickyBottom: {
        paddingHorizontal: 20,
        paddingTop: 12,
        paddingBottom: 28,
        backgroundColor: "#FFFFFF",
        borderTopWidth: 1,
        borderTopColor: "#EDE9FE"
    },
    saveBtn: {
        backgroundColor: "#059669",
        paddingVertical: 14,
        borderRadius: 20,
        alignItems: "center"
    },
    saveBtnText: { fontSize: 15, fontWeight: "700", color: "#FFFFFF" },
    snackbar: { backgroundColor: "#1F1547" }
});
